import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Iter54b-ext — MediaCarousel
 * ────────────────────────────────────────────────────────────────────────────
 * Generic, headless(ish) carousel primitive so the same behaviour can power
 * every homepage carousel (hero, featured plans strip, testimonials, blogs,
 * interactive experiences, walkthroughs, 360° tours, …). Zero deps.
 *
 * The parent supplies:
 *   slides         array of anything — the primitive only cares about
 *                  length and delegates rendering
 *   renderSlide    ({ slide, index, isActive, isFirst }) => ReactNode
 *   config         { auto_rotate_seconds, infinite_loop, pause_on_hover,
 *                    pause_on_tab_inactive, show_arrows, show_dots,
 *                    mobile_swipe }
 *   heightClass    Tailwind height utility (default: h-[86vh] min-h-[620px]
 *                  max-h-[900px]) — lets us reuse the primitive at smaller
 *                  sizes for testimonials / blogs.
 *   ariaLabel      accessible label for the region
 *   testidPrefix   prefix for data-testids (hero / featured / testimonials …)
 *   onActiveChange (index, slide) callback — used by the hero to fire
 *                  impression analytics.
 *
 * The primitive is intentionally opinionated about *fade / dot / arrow*
 * ergonomics and lets the caller layer any transition on the rendered
 * slide (via `renderSlide` receiving `isActive`). This keeps the primitive
 * transition-agnostic yet consistent across all reuses.
 */

const MediaCarousel = ({
  slides,
  renderSlide,
  config = {},
  heightClass = "h-[86vh] min-h-[620px] max-h-[900px]",
  ariaLabel = "Featured slides",
  testidPrefix = "carousel",
  onActiveChange,
}) => {
  const count = (slides || []).length;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const rootRef = useRef(null);

  const autoSec = Number(config.auto_rotate_seconds || 0);
  const loop = config.infinite_loop !== false;
  const showArrows = config.show_arrows !== false;
  const showDots = config.show_dots !== false;
  const swipe = config.mobile_swipe !== false;
  const pauseHover = config.pause_on_hover !== false;
  const pauseInactive = config.pause_on_tab_inactive !== false;

  const goTo = useCallback(
    (i) => {
      if (count === 0) return;
      const next = loop ? (i + count) % count : Math.max(0, Math.min(count - 1, i));
      setIdx(next);
    },
    [count, loop]
  );
  const next = useCallback(() => goTo(idx + 1), [idx, goTo]);
  const prev = useCallback(() => goTo(idx - 1), [idx, goTo]);

  useEffect(() => {
    if (!autoSec || count <= 1 || paused || tabHidden) return undefined;
    const t = setInterval(() => setIdx((i) => (loop ? (i + 1) % count : Math.min(count - 1, i + 1))), autoSec * 1000);
    return () => clearInterval(t);
  }, [autoSec, count, paused, tabHidden, loop]);

  useEffect(() => {
    if (!pauseInactive) return undefined;
    const onVis = () => setTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    setTabHidden(document.hidden);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [pauseInactive]);

  useEffect(() => {
    if (idx >= count) setIdx(0);
  }, [count, idx]);

  useEffect(() => {
    if (count > 0 && typeof onActiveChange === "function") {
      onActiveChange(idx, slides[idx]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, count]);

  if (count === 0) return null;

  const onTouchStart = (e) => swipe && setTouchStartX(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (!swipe || touchStartX == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    setTouchStartX(null);
  };

  return (
    <section
      ref={rootRef}
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      data-testid={`${testidPrefix}-section`}
      className="relative isolate"
      onMouseEnter={() => pauseHover && setPaused(true)}
      onMouseLeave={() => pauseHover && setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className={`relative w-full ${heightClass} overflow-hidden bg-primary`}>
        {slides.map((slide, i) => (
          <React.Fragment key={slide.id || i}>
            {renderSlide({ slide, index: i, isActive: i === idx, isFirst: i === 0 })}
          </React.Fragment>
        ))}

        {showArrows && count > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Previous slide"
              data-testid={`${testidPrefix}-prev`}
              className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-background/20 hover:bg-background/40 backdrop-blur border border-primary-foreground/30 text-primary-foreground transition-fast z-30"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={next}
              aria-label="Next slide"
              data-testid={`${testidPrefix}-next`}
              className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-background/20 hover:bg-background/40 backdrop-blur border border-primary-foreground/30 text-primary-foreground transition-fast z-30"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {showDots && count > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-30" data-testid={`${testidPrefix}-dots`}>
            {slides.map((_, i) => (
              <button
                key={`dot-${i}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`${testidPrefix}-dot-${i}`}
                className={`h-[2px] transition-fast ${i === idx ? "w-12 bg-accent" : "w-6 bg-primary-foreground/40"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default MediaCarousel;
